import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { Badge, Button, Card, Dot } from '@ygtechnologies/rubus-ui';
import Tags from '@/components/Tags';

import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { ProductThumbnails } from '@/seller/components/products/ProductThumbnails';
import { ConfiguratorGroupDto, ConfiguratorRoomDto } from '@/services/configurator';

import { GENERAL_ROOM_ID, getRoomDisplayName } from '../../utils/configuratorRooms';
import { useOptionConfigurator } from '@/components/OptionConfigurator/OptionConfiguratorContext';
import { useImageUrls } from '@/components/OptionConfigurator/useImageUrls';
import { ShowroomHidden } from '@/components/OptionConfigurator/ShowroomVisibility';
import { useOptionsScope } from '../OptionsScope';
import { SelectionWizardDialog } from '../selectionWizard/SelectionWizardDialog';
import { OptionRowStatus } from './OptionRowStatus';
import theme from './OptionRow.module.scss';

interface Props {
    room: ConfiguratorRoomDto;
    group: ConfiguratorGroupDto;
    deadlineDate: Date | string;
    onSelectionChanged?: () => void;
}

export const OptionRow: React.FC<Props> = ({ room, group, deadlineDate, onSelectionChanged }) => {
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const { readOnly } = useOptionsScope();
    const { isShowroom } = useOptionConfigurator();
    const { getProjectCatalogProductFileThumbnailUrl, getProjectCatalogProductFileUrl } = useImageUrls();

    if (group.overridden) {
        return (
            <Card variant="flat" className={theme.overridden}>
                <div className={theme.image} />
                <div className={theme.main}>
                    <h4 className={theme.heading}>{group.name}</h4>
                    <span>
                        <Trans>This option was overridden by an external agreement or a change request</Trans>
                    </span>
                </div>
            </Card>
        );
    }

    const selected = group.selectedOption;
    // No selection yet — fall back to the default option's product so we show a real thumbnail.
    const defaultOption = group.options.find((o) => o.isDefault);
    const thumbnailImages = (selected ?? defaultOption)?.product?.images ?? [];
    const itemCategoryId = selected?.product?.itemCategoryId ?? 0;
    // In the showroom, deadlines/signing are irrelevant, so locked/expired groups stay editable.
    const canChange = !readOnly && group.applicable && (isShowroom || !group.isLocked);
    const roomName = getRoomDisplayName(room);
    // The synthetic "General" bucket isn't a real room — don't surface it as a tag.
    const showRoomBadge = room.roomId !== GENERAL_ROOM_ID && !!roomName;

    return (
        <>
            <SelectionWizardDialog
                buyerOptionGroupId={group.id}
                roomId={room.roomId}
                isOpen={isWizardOpen}
                onClose={() => setIsWizardOpen(false)}
                onSelectionChanged={onSelectionChanged}
            />
            <Card variant="flat" className={theme.row}>
                <ProductThumbnails
                    images={thumbnailImages}
                    thumbnailUrlGenerator={getProjectCatalogProductFileThumbnailUrl}
                    imageUrlGenerator={getProjectCatalogProductFileUrl}
                    className={theme.image}
                />
                <div className={theme.main}>
                    <div className={theme.info}>
                        {itemCategoryId > 0 && (
                            <span className={theme.category}>
                                <ItemCategoryDisplay value={itemCategoryId} />
                            </span>
                        )}
                        <button type="button" className={theme.heading} onClick={() => setIsWizardOpen(true)}>
                            {group.name}
                        </button>
                        {selected ? (
                            <div className={theme.choice}>
                                <Trans>Your choice:</Trans> <strong>{selected.productName}</strong>{' '}
                                <i>{selected.customFieldData ?? ''}</i>
                            </div>
                        ) : (
                            <div className={theme.noChoice}>
                                <span className={theme.noChoiceText}>
                                    <Trans>None selected</Trans>
                                </span>
                                <Dot variant="warning" />
                            </div>
                        )}
                        {(showRoomBadge || group.tags.length > 0) && (
                            <div className={theme.tagRow}>
                                {showRoomBadge && (
                                    <Badge variant="neutral" appearance="subtle">
                                        {roomName}
                                    </Badge>
                                )}
                                {group.tags.length > 0 && (
                                    <Tags
                                        size="small"
                                        tags={group.tags.map((tag) => ({ id: tag.id, label: tag.name, sortOrder: 0 }))}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <div className={theme.right}>
                        {!group.hidePrice && selected?.price && (
                            <div className={theme.price}>
                                <BuyerCurrencyDisplay value={selected.price.finalPrice} alwaysShowSign />
                            </div>
                        )}
                        <ShowroomHidden>
                            <OptionRowStatus group={group} deadlineDate={deadlineDate} />
                        </ShowroomHidden>
                        <Button variant="tertiary" size="sm" className={theme.button} onClick={() => setIsWizardOpen(true)}>
                            {canChange ? <Trans>Change option</Trans> : <Trans>View option</Trans>}
                        </Button>
                    </div>
                </div>
            </Card>
        </>
    );
};
